import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { recordAskFrankClick, reportAskFrankResults } from '../../services/api';
import FranklyNetEntityCard from './FranklyNetEntityCard';
import { frameUrl, frameArticle } from './frameLinks';

const PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0,
    imageRendering: 'pixelated',
};

const SECTION_LABELS = {
    official: 'Habbo',
    frankly: 'Frankly',
    fansite: 'Fansite',
};

const AGENT_VERBS = {
    host: 'ran',
    winner: 'won',
    creator: 'made',
};

function FrankAnswer({ answer }) {
    if (!answer) return null;

    const toneClass = answer.confidence === 'good'
        ? 'border-l-[6px] border-maroon'
        : answer.confidence === 'none'
            ? 'border-l-[6px] border-gray-400'
            : 'border-l-[6px] border-gold';

    return (
        <div className={`bg-offwhite border border-gray-300 ${toneClass} shadow-[0_3px_0_0_rgba(0,0,0,0.35)]`}>
            <div className="flex items-start gap-3 p-4">
                <img
                    src={`${process.env.PUBLIC_URL}/img/Frank_05.png`}
                    alt="Frank"
                    draggable={false}
                    className="hidden sm:block"
                    style={PIXEL_IMAGE}
                />

                <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                        <span className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-maroon">
                            Frank says
                        </span>
                        {answer.confidence === 'thin' && (
                            <span className="bg-yellow-100 px-2 py-[1px] text-[10px] font-bold uppercase text-yellow-700">
                                Best guess
                            </span>
                        )}

                        {answer.period && (
                            <span
                                title={`Frank read your question as being about ${answer.period.label}`}
                                className="border border-maroon/30 bg-maroon/10 px-2 py-[1px] text-[10px] font-bold uppercase text-maroon"
                            >
                                {answer.period.label}
                                {answer.period.range && answer.period.range !== answer.period.label
                                    ? ` | ${answer.period.range}`
                                    : ''}
                            </span>
                        )}

                        {answer.agent && (
                            <span
                                title={`Frank read your question as asking who ${AGENT_VERBS[answer.agent.role] || 'ran'} ${answer.agent.subject}`}
                                className="border border-maroon/30 bg-maroon/10 px-2 py-[1px] text-[10px] font-bold uppercase text-maroon"
                            >
                                {answer.agent.subject}
                            </span>
                        )}
                    </div>

                    <p className="text-[15px] leading-relaxed text-gray-800">{answer.text}</p>
                </div>
            </div>
        </div>
    );
}

function ReportResults({ query, answer, results }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        setSending(true);

        const res = await reportAskFrankResults({
            query,
            note,
            intent: answer?.questionType || null,
            answer: answer?.text || null,
            results: (results || []).slice(0, 5).map(item => ({ title: item.title, url: item.url })),
        });

        setSending(false);

        if (res.success) {
            setSent(true);
            setOpen(false);
            setNote('');
        }
    };

    if (sent) {
        return <span className="text-white/70">Thanks, passed to the desk.</span>;
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="underline underline-offset-4 hover:text-white"
            >
                Are these results wrong?
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setOpen(false)}
                >
                    <form
                        onSubmit={submit}
                        onClick={(event) => event.stopPropagation()}
                        className="w-full max-w-[460px] border border-gray-300 bg-offwhite p-4 shadow-[0_4px_0_0_rgba(0,0,0,0.4)]"
                    >
                        <p className="font-['Jersey_10'] text-[20px] uppercase tracking-[1px] text-maroon">
                            Tell Frank what went wrong
                        </p>
                        <p className="mt-1 text-[13px] leading-snug text-gray-600">
                            You asked &ldquo;{query}&rdquo;. The question and the results you were shown go
                            with your note, so there is no need to repeat them.
                        </p>

                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            maxLength={800}
                            rows={4}
                            autoFocus
                            placeholder="What were you expecting to see instead?"
                            className="mt-3 w-full border border-gray-300 bg-white px-3 py-2 text-[14px] outline-none focus:border-maroon"
                        />

                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="border border-gray-300 bg-white px-3 py-2 text-[12px] uppercase text-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={sending}
                                className="border border-black/30 bg-maroon px-4 py-2 font-['Jersey_10'] text-[16px] uppercase text-white disabled:opacity-50"
                            >
                                {sending ? 'Sending...' : 'Send to the desk'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}

function SuggestionTile({ option, onSearch }) {
    return (
        <button
            type="button"
            onClick={() => onSearch(option.query || option.name)}
            title={[option.name, option.shape, option.detail, option.motto].filter(Boolean).join(' | ')}
            className="flex w-[160px] flex-col items-center gap-1 border border-gray-300 bg-offwhite p-2 text-center hover:bg-gray-100"
        >
            <span className="flex h-[64px] w-full items-center justify-center overflow-hidden">
                {option.imageUrl ? (
                    <img
                        src={option.imageUrl}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        style={PIXEL_IMAGE}
                    />
                ) : (
                    <span className="flex h-[40px] w-[40px] items-center justify-center bg-gray-200 font-['Jersey_10'] text-[18px] uppercase text-gray-500">
                        {String(option.name || '?').charAt(0)}
                    </span>
                )}
            </span>

            <span className="text-[12px] leading-tight text-maroon">{option.name}</span>

            {option.detail && (
                <span className="text-[10px] leading-tight text-gray-500">{option.detail}</span>
            )}
        </button>
    );
}

function DidYouMean({ suggestions, onSearch, period }) {
    if (!suggestions || suggestions.length === 0) return null;

    const heading = period && suggestions[0]?.fromWindow
        ? `Released ${period.phrase || period.label}`
        : 'Did you mean one of these?';

    return (
        <div className="mt-5 border border-gray-300 bg-white p-5 shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
            <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                {heading}
            </p>

            <div className="flex flex-wrap gap-2">
                {suggestions.map(option => (
                    <SuggestionTile key={option.id || option.name} option={option} onSearch={onSearch} />
                ))}
            </div>
        </div>
    );
}

function ResultRow({ result, query, featured = false }) {
    const target = result.articleSlug
        ? frameArticle({ slug: result.articleSlug, habboId: result.habboId }, query)
        : frameUrl(result.url, query);

    let displayUrl = result.url;
    try {
        const parsed = new URL(result.url);
        displayUrl = `${parsed.hostname}${parsed.pathname === '/' ? '' : parsed.pathname}`;
    } catch {
        displayUrl = result.url;
    }

    const published = result.publishedAt
        ? new Date(result.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;

    const onOpen = () => {
        recordAskFrankClick(result.pageId).catch(() => {});
    };

    return (
        <div className={featured ? '' : 'border-b border-gray-200 pb-4 last:border-b-0 last:pb-0'}>
            <div className="mb-[2px] flex flex-wrap items-center gap-2">
                {result.faviconUrl && (
                    <img src={result.faviconUrl} alt="" aria-hidden="true" width={16} height={16} className="shrink-0" />
                )}
                <span className="truncate text-[12px] text-green-800">{displayUrl}</span>

                {result.section && (
                    <span className="shrink-0 border border-gray-300 px-[5px] py-[1px] text-[9px] font-bold uppercase text-gray-500">
                        {SECTION_LABELS[result.section]}
                    </span>
                )}

                {result.articleSlug ? (
                    <span className="shrink-0 bg-maroon px-[6px] py-[1px] text-[9px] font-bold uppercase text-white" title="Official Habbo article, readable here in AskFrank">
                        Official
                    </span>
                ) : result.iframeAllowed === false && (
                    <span className="shrink-0 text-[10px] uppercase text-gray-400" title="This site cannot be framed, it will open in a new tab">
                        external
                    </span>
                )}

                {published && (
                    <span className="shrink-0 text-[11px] text-gray-500">{published}</span>
                )}
            </div>

            <Link
                to={target}
                onClick={onOpen}
                className={`font-['Jersey_10'] uppercase leading-tight tracking-[1px] text-maroon hover:underline ${featured ? 'text-[24px]' : 'text-[20px]'}`}
            >
                {result.title}
            </Link>

            <p className="mt-1 text-[14px] leading-snug text-gray-700">{result.snippet}</p>

            <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                <span>{result.sourceName}</span>
                <span>|</span>
                <span className="capitalize">{result.sourceType}</span>
            </div>
        </div>
    );
}

function ResultList({ rows, query, total, heading, aside }) {
    if (rows.length === 0) return null;

    return (
        <div className="mt-6">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white/90"
                   style={{ textShadow: '-1px 0 0 rgba(0,0,0,0.6), 1px 0 0 rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.6)' }}>
                    {heading}
                </p>
                <span className="flex items-baseline gap-3 text-[11px] text-white/60">
                    <span>{total} result{total === 1 ? '' : 's'}</span>
                    {aside}
                </span>
            </div>

            <div className="flex flex-col gap-4 border border-gray-300 bg-white p-5 shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
                {rows.map(result => (
                    <ResultRow key={result.pageId} result={result} query={query} />
                ))}
            </div>
        </div>
    );
}

function FranklyNetResults({
    query, answer, entity, results, suggestions,
    pagination, loading, error, onPage, onSearch,
}) {
    if (loading) {
        return (
            <div className="border border-gray-300 bg-white py-16 text-center">
                <img
                    src={`${process.env.PUBLIC_URL}/img/frankly-ducks.gif`}
                    alt="Loading"
                    draggable={false}
                    className="mx-auto"
                    style={{ imageRendering: 'pixelated' }}
                />
                <p className="mt-2 font-['Jersey_10'] text-[18px] uppercase text-gray-500">
                    Frank is having a look...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-gray-300 bg-white p-6 text-center">
                <p className="font-['Jersey_10'] text-[20px] uppercase text-maroon">Frank could not answer</p>
                <p className="mt-2 text-[14px] text-gray-600">{error}</p>
            </div>
        );
    }

    const firstPage = (pagination.page || 1) === 1;
    const [best, ...rest] = results;
    const listed = firstPage ? rest : results;

    const nothing = results.length === 0 && !entity && (!suggestions || suggestions.length === 0);

    return (
        <div className="mx-auto max-w-[900px]">
            <FrankAnswer answer={answer} />

            <FranklyNetEntityCard entity={entity} query={query} />

            <DidYouMean suggestions={suggestions} onSearch={onSearch} period={answer?.period} />

            {nothing ? (
                <div className="mt-5 border border-gray-300 bg-white p-6 shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
                    <p className="font-['Jersey_10'] text-[18px] uppercase text-gray-700">
                        No pages matched that one
                    </p>
                    <p className="mt-2 text-[14px] text-gray-600">
                        Try fewer words, or ask it a different way. Frank only reads sites he has been approved to read.
                    </p>
                </div>
            ) : results.length === 0 ? null : (
                <>
                    {firstPage && (
                        <div className="mt-5 border border-gray-300 bg-white p-5 shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
                            <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                                Most likely what you meant
                            </p>
                            <ResultRow result={best} query={query} featured />
                        </div>
                    )}

                    <ResultList
                        rows={listed}
                        query={query}
                        total={pagination.total}
                        heading={firstPage ? 'Other things that might help' : 'More results'}
                        aside={<ReportResults query={query} answer={answer} results={results} />}
                    />

                    {pagination.totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-center gap-2">
                            <button
                                type="button"
                                onClick={() => onPage(pagination.page - 1)}
                                disabled={pagination.page <= 1}
                                className="border border-gray-300 bg-white px-3 py-1 text-[13px] uppercase disabled:opacity-40"
                            >
                                Prev
                            </button>
                            <span className="text-[13px] text-white/80">
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => onPage(pagination.page + 1)}
                                disabled={pagination.page >= pagination.totalPages}
                                className="border border-gray-300 bg-white px-3 py-1 text-[13px] uppercase disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default FranklyNetResults;
